import { LucideCheck, LucideChevronDown } from 'lucide-react';
import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { cn } from '../../../lib/utils';

type SelectProps = {
  mode?: 'default' | 'search';
  value: any;
  items: any[];
  className?: string;
  onSelect?(item: any): void;
  disabled?: boolean;
  onChange?(val: any): void;
  dataQa?: string;
};

const Select = (props: SelectProps) => {
  const {
    mode = 'default',
    value = '',
    items = [],
    className = '',
    onSelect,
    disabled = false,
    onChange,
    dataQa = ''
  } = props;

  let selectRef = useRef<any>(null);

  const [openDropdown, setOpenDropdown] = useState<boolean>(false);
  const [search, setSearch] = useState<string>(value);
  const [elementItems, setElementItems] = useState<any[]>([]);

  useEffect(() => {
    setElementItems(items);
  }, [items]);

  useEffect(() => {
    setSearch(value);
  }, [value]);

  const handleFocus = () => {
    setOpenDropdown(true);
    // Uncomment if the filtering on focus is required
    // setElementItems(() => {
    //   if (!search) return items;
    //   return items?.filter((f: any) =>
    //     typeof f === 'string'
    //       ? f?.includes(search)
    //       : f?.label?.toLowerCase()?.includes(search?.toLowerCase() || '') ||
    //         f?.name?.toLowerCase()?.includes(search?.toLowerCase() || '') ||
    //         f?.value?.toLowerCase()?.includes(search?.toLowerCase() || '') ||
    //         f?.displayName?.toLowerCase()?.includes(search?.toLowerCase() || '') ||
    //         f?.pattern?.toLowerCase()?.includes(search?.toLowerCase() || '')
    //   );
    // });
  };

  const handleBlur = (e: any) => {
    if (selectRef?.current && !selectRef?.current?.contains(e.relatedTarget)) {
      const autoSelect = items?.find((f) =>
        typeof f === 'string'
          ? f === search
          : f?.label === search ||
            f?.name === search ||
            f?.value === search ||
            f?.displayName === search ||
            f?.pattern === search
      );
      if (autoSelect && onSelect) {
        onSelect(autoSelect);
      }
    }
  };

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    let { value } = e.target;
    setSearch(value);
    setElementItems(() => {
      if (!value) return items;
      return items?.filter((f: any) =>
        typeof f === 'string'
          ? f?.includes(value)
          : f?.label?.toLowerCase()?.includes(value?.toLowerCase() || '') ||
            f?.name?.toLowerCase()?.includes(value?.toLowerCase() || '') ||
            f?.value?.toLowerCase()?.includes(value?.toLowerCase() || '') ||
            f?.displayName?.toLowerCase()?.includes(value?.toLowerCase() || '') ||
            f?.pattern?.toLowerCase()?.includes(value?.toLowerCase() || '')
      );
    });
    if (onChange) {
      onChange(value);
    }
  };

  const handleOpenDropdown = () => {
    setOpenDropdown((prev) => !prev);
  };

  const handleChooseItem = (item: any) => {
    if (onSelect) {
      onSelect(item);
    }
    setOpenDropdown(false);
  };

  const handleClickaway = (e: any) => {
    if (selectRef?.current && !selectRef?.current?.contains(e.target)) {
      setOpenDropdown(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickaway);
    return () => {
      document.removeEventListener('mousedown', handleClickaway);
    };
  }, []);

  return (
    <div ref={selectRef} className="relative">
      {mode === 'search' ? (
        <div className="relative">
          <input
            data-qa={dataQa}
            autoFocus={false}
            disabled={disabled}
            className={cn(
              `text-left min-h-[40px] file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground border-input h-9 w-full min-w-0 rounded-md border bg-white px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive`,
              className
            )}
            value={search}
            onChange={handleSearch}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder="Select"
          />
          <LucideChevronDown className="pointer-events-none select-none lucide lucide-chevron-down h-4 w-4 opacity-50 absolute top-1/2 right-[8px] -translate-y-1/2" />
        </div>
      ) : (
        <div className="relative">
          <button
            data-qa={dataQa}
            autoFocus={false}
            disabled={disabled}
            className={cn(
              `text-left min-h-[40px] file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground border-input h-9 w-full min-w-0 rounded-md border bg-white px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive`,
              className
            )}
            onClick={handleOpenDropdown}
            type="button"
          >
            {search || 'Select'}
          </button>
          <LucideChevronDown className="pointer-events-none select-none lucide lucide-chevron-down h-4 w-4 opacity-50 absolute top-1/2 right-[8px] -translate-y-1/2" />
        </div>
      )}

      <div
        data-opened={openDropdown}
        className="absolute z-[1] top-[110%] transition outline-none bg-popover text-popover-foreground w-full origin-center rounded-md border border-input p-[4px] shadow-md data-[opened=false]:opacity-0 data-[opened=true]:opacity-100 data-[opened=true]:animate-animate-in data-[opened=false]:select-none data-[opened=false]:pointer-events-none data-[opened=false]:animate-animate-out data-[opened=true]:animate-slide-in-from-top-2 max-h-[300px] overflow-y-auto"
      >
        {elementItems?.length > 0 ? (
          elementItems?.map((item: any, index: number) => {
            const displayValue =
              typeof item === 'string'
                ? item
                : item?.label ||
                  item?.name ||
                  item?.value ||
                  item?.displayName ||
                  item?.pattern ||
                  '';
            return (
              <div key={index}>
                <button
                  data-qa={`${dataQa}_item_${displayValue}`}
                  data-selected={value === displayValue}
                  className="flex items-center gap-2 w-full text-[14px] px-2 py-1.5 rounded-sm hover:bg-muted cursor-pointer data-[selected=true]:bg-accent"
                  onClick={() => handleChooseItem(item)}
                  type="button"
                >
                  <LucideCheck
                    className={`w-4 h-4 ${value === displayValue ? 'opacity-100' : 'opacity-0'}`}
                  />
                  {displayValue}
                </button>
              </div>
            );
          })
        ) : (
          <div className="flex items-center gap-2 w-full text-[14px] px-2 py-1.5 rounded-sm">
            <span>No states found.</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default Select;
